import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { useToast } from '@/components/ui'
import { projectService } from '@/services/http/projectService'

import type { GetProjectHistoryResponse } from '@/services/http/projectService/project/getProjectHistory'
import type { QueryParams } from '@/utils/queryParams'

type Props = {
  query: QueryParams & { projectId: string }
}

export const usePortfolioProjectHistory = ({ query }: Props) => {
  const { page = 1, limit = 5, projectId } = query
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const cacheTime = 5 * 60 * 1000

  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: ['portfolioProjectHistory', { projectId, page, limit }],
    queryFn: () =>
      projectService.project.getProjectHistory({ projectId, limit, page }),
    staleTime: cacheTime,
    gcTime: cacheTime * 2,
  })

  useEffect(() => {
    if (isError)
      toast({
        title: 'Erro ao carregar o histórico do projeto',
        variant: 'destructive',
      })
  }, [isError, toast])

  const fetchPage = async (
    pageNumber: number,
  ): Promise<GetProjectHistoryResponse | undefined> => {
    const queryKey = [
      'portfolioProjectHistory',
      { projectId, page: pageNumber, limit },
    ]

    const cachedData =
      queryClient.getQueryData<GetProjectHistoryResponse>(queryKey)
    if (cachedData) {
      return cachedData
    }

    return await queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        projectService.project.getProjectHistory({
          projectId,
          limit,
          page: pageNumber,
        }),
      staleTime: 5 * 60 * 1000,
    })
  }

  if (data) {
    return {
      history: data,
      isLoading,
      refetchProjectHistory: refetch,
      fetchPage,
    }
  }

  return {
    history: null,
    isLoading,
    refetchProjectHistory: refetch,
    fetchPage,
  }
}
